function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

//Drawer used for mobile navigation and mini cart
class SideDrawerToggle extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", function (evt) {
      evt.preventDefault();

      // Close all drawers
      if (this.getAttribute("data-drawer") == "all") {
        const activeDrawers = document.querySelectorAll("side-drawer.active");
        if (activeDrawers) {
          activeDrawers.forEach((el) => {
            //Toggle drawer event
            const event = new CustomEvent("drawer-toggle", {
              detail: { trigger: this },
            });
            el.dispatchEvent(event);
          });
        }
      } else {
        const drawer = document.getElementById(this.getAttribute("data-drawer"));
        //Toggle drawer event
        const event = new CustomEvent("drawer-toggle", {
          detail: { trigger: this },
        });
        drawer.dispatchEvent(event);
      }
    });
  }
}

customElements.define("side-drawer-toggle", SideDrawerToggle);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this._wasOpen = false;

    const thisElement = this;

    this.observer = new MutationObserver(() => {
      if (this._wasOpen && !this.hasAttribute("drawer-open")) {
        thisElement.open(this);
      }
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ["drawer-open"],
    });

    //Toggle event listeners
    this.addEventListener("drawer-toggle", function (event) {
      if (this.hasAttribute("drawer-open")) {
        thisElement.close(this);
      } else {
        thisElement.open(event);
      }
    });

    //Key up and body click events
    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  //Open drawer
  open(event) {
    this.openedBy = event?.detail?.trigger || this;
    this._wasOpen = true;
    this.setAttribute("drawer-open", "");
    this.classList.add("active");
    document.body.classList.add("blur");
    document.body.classList.add("drawer--active");

    if (this.classList.contains("drawer--right")) {
      document.body.classList.add("drawer--right-position");
    }

    if (this.classList.contains("drawer--left")) {
      document.body.classList.add("drawer--left-position");
    }

    this.addEventListener(
      "transitionend",
      () => {
        this.focus();
        trapFocus(event.target);
      },
      { once: true }
    );

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });
  }

  //Close drawer
  close() {
    this._wasOpen = false;
    this.removeAttribute("drawer-open");
    this.classList.remove("active");
    const activeDrawers = document.querySelectorAll(".drawer.active");
    if (activeDrawers.length == 0) {
      document.body.classList.remove("blur");
      document.body.classList.remove("drawer--active");
      document.body.classList.remove("drawer--right-position");
      document.body.classList.remove("drawer--left-position");
    }

    document.body.removeEventListener("click", this.onBodyClick, {
      passive: true,
    });
    removeTrapFocus(this);
  }

  //Body click event to close drawer
  handleBodyClick(evt) {
    const target = evt.target;

    if (!target || !target.isConnected) return;

    const drawerElement = target.closest("side-drawer");
    const isInsideAnyDrawer = drawerElement !== null;
    const isOnToggle = target.closest("side-drawer-toggle") !== null;

    if (!isInsideAnyDrawer && !isOnToggle) {
      this.close();
    }
  }
}

customElements.define("side-drawer", SideDrawer);

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class AccordionCustom extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.controller = new AbortController();
  }

  connectedCallback() {
    if (!this.details || !this.summary) return;

    this.setDefaultOpenState();
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  addEventListeners() {
    const { signal } = this.controller;

    this.addEventListener("keydown", (e) => this.handleKeyDown(e), { signal });
    this.summary.addEventListener("click", (e) => this.handleClick(e), {
      signal,
    });
    window.matchMedia("(min-width: 990px)").addEventListener("change", () => this.setDefaultOpenState(), { signal });
  }

  handleClick(event) {
    const isMobile = window.innerWidth < 750;
    const isDesktop = !isMobile;

    if ((isMobile && this.dataset.disableOnMobile === "true") || (isDesktop && this.dataset.disableOnDesktop === "true")) {
      event.preventDefault();
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape" && this.dataset.closeWithEscape === "true") {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }

  setDefaultOpenState() {
    const isMobile = window.innerWidth < 750;
    this.details.open = (isMobile && this.hasAttribute("open-by-default-on-mobile")) || (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}

window.customElements.define(
  "layout-toggle",
  class GridToggle extends HTMLElement {
    constructor() {
      super();
      this.largeGridViewButton = this.querySelector("#largeGridViewButton");
      this.mediumGridViewButton = this.querySelector("#mediumGridViewButton");
      this.smallGridViewButton = this.querySelector("#smallGridViewButton");
      this.loader = document.querySelector("loading-button");
      this.loadedProducts = false;

      this.largeGridViewButton?.addEventListener("click", this.onButtonClick.bind(this));
      this.mediumGridViewButton?.addEventListener("click", this.onButtonClick.bind(this));
      this.smallGridViewButton?.addEventListener("click", this.onButtonClick.bind(this));

      const gridView = localStorage.getItem("layout-grid-option");

      if (gridView == "grid--large") {
        this.largeGridViewButton.click();
      } else if (gridView == "grid--medium") {
        this.mediumGridViewButton.click();
      } else if (gridView == "grid--small") {
        this.smallGridViewButton.click();
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      const colID = event.currentTarget.id;
      if (colID == "largeGridViewButton") {
        const largeGridViewButton = document.querySelectorAll("#largeGridViewButton");
        const mediumGridViewButton = document.querySelectorAll("#mediumGridViewButton");
        const smallGridViewButton = document.querySelectorAll("#smallGridViewButton");

        largeGridViewButton.forEach((element) => {
          document.getElementById("ProductGridContainer").classList.add("grid--large");
          document.getElementById("ProductGridContainer").classList.remove("grid--medium");
          document.getElementById("ProductGridContainer").classList.remove("grid--small");
          element.classList.add("active");
        });

        mediumGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        smallGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-grid-option", "grid--large");
      } else if (colID == "mediumGridViewButton") {
        const largeGridViewButton = document.querySelectorAll("#largeGridViewButton");
        const mediumGridViewButton = document.querySelectorAll("#mediumGridViewButton");
        const smallGridViewButton = document.querySelectorAll("#smallGridViewButton");

        mediumGridViewButton.forEach((element) => {
          document.getElementById("ProductGridContainer").classList.remove("grid--large");
          document.getElementById("ProductGridContainer").classList.add("grid--medium");
          document.getElementById("ProductGridContainer").classList.remove("grid--small");

          element.classList.add("active");
        });

        largeGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        smallGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-grid-option", "grid--medium");
      } else if (colID == "smallGridViewButton") {
        const largeGridViewButton = document.querySelectorAll("#largeGridViewButton");
        const mediumGridViewButton = document.querySelectorAll("#mediumGridViewButton");
        const smallGridViewButton = document.querySelectorAll("#smallGridViewButton");

        smallGridViewButton.forEach((element) => {
          document.getElementById("ProductGridContainer").classList.remove("grid--large");
          document.getElementById("ProductGridContainer").classList.remove("grid--medium");
          document.getElementById("ProductGridContainer").classList.add("grid--small");

          element.classList.add("active");
        });

        largeGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        mediumGridViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-grid-option", "grid--small");

        if (!this.loader || this.loadedProducts) return;

        setTimeout(() => {
          if (this.loader.loadedProducts > this.loader.loadButton.getAttribute("data-loaded-products-total")) return;
          this.loader.loadMore();
          this.loadedProducts = true;
        }, 50);
      }
    }
  }
);

window.customElements.define(
  "image-toggle",
  class ImageToggle extends HTMLElement {
    constructor() {
      super();
      this.modelViewButton = this.querySelector("#modelViewButton");
      this.productViewButton = this.querySelector("#productViewButton");

      this.modelViewButton?.addEventListener("click", this.onButtonClick.bind(this));
      this.productViewButton?.addEventListener("click", this.onButtonClick.bind(this));

      const gridView = localStorage.getItem("layout-image-option");

      if (gridView == "grid--model") {
        this.modelViewButton.click();
      } else if (gridView == "grid--product") {
        this.productViewButton.click();
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      const colID = event.currentTarget.id;
      if (colID == "modelViewButton") {
        const modelViewButton = document.querySelectorAll("#modelViewButton");
        const productViewButton = document.querySelectorAll("#productViewButton");

        modelViewButton.forEach((element) => {
          document.getElementById("ProductGridContainer").classList.add("grid--model");
          document.getElementById("ProductGridContainer").classList.remove("grid--product");
          element.classList.add("active");
        });

        productViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-image-option", "grid--model");
      } else if (colID == "productViewButton") {
        const modelViewButton = document.querySelectorAll("#modelViewButton");
        const productViewButton = document.querySelectorAll("#productViewButton");

        productViewButton.forEach((element) => {
          document.getElementById("ProductGridContainer").classList.remove("grid--model");
          document.getElementById("ProductGridContainer").classList.add("grid--product");
          element.classList.add("active");
        });

        modelViewButton.forEach((element) => {
          element.classList.remove("active");
        });

        localStorage.setItem("layout-image-option", "grid--product");
      }
    }
  }
);

class loadingButton extends HTMLElement {
  constructor() {
    super();

    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    this.overlay = document.getElementById("loading-overlay");
    if (!this.overlay) {
      this.overlay = document.createElement("div");
      this.overlay.id = "loading-overlay";
      this.overlay.innerHTML = '<div class="spinner"></div>';
      document.body.appendChild(this.overlay);
    }

    // State
    this.url = "";
    this.totalProducts = 0;
    this.loadedProducts = 0;
    this.loadedPages = new Set([1]); // track which pages have been rendered
    this.firstRenderedPage = 1; // lowest page currently in the DOM
    this.lastRenderedPage = 1; // last page in the DOM

    // Elements
    this.progressBar = document.querySelector(".progress-bar");
    this.resultCount = document.querySelector(".result-count");
    this.automaticLoading = this.getAttribute("automatic-loading");
    this.loadButtonContainer = this.closest(".pagination-wrapper");
    this.loadButton = this.querySelector("button");
    this.productContainer = document.querySelector("#ProductGridContainer");
    this.articleContainer = document.querySelector("#ArticleGridContainer");
    this.vanillaPagination = document.querySelector("#pagination");

    // Event listeners
    this.loadButton.addEventListener("click", this.loadMore.bind(this));

    let options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.25,
    };
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), options);
  }

  // Go to previously clicked product
  async goToProduct(handle, targetPage) {
    // Build URL for the target page
    const url = new URL(window.location.href);
    url.searchParams.set("page", targetPage);

    this.url = url.href;
    history.replaceState({ path: url.href }, "", url.href);

    // Fetch the target page HTML and render its grid
    const response = await fetch(url.href);
    const htmlText = await response.text();
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(htmlText, "text/html");

    // Replace product grid with the fetched page content
    const newGrid = newDoc.getElementById("ProductGridContainer");
    if (newGrid && this.productContainer) {
      this.productContainer.innerHTML = newGrid.innerHTML;
    }
    // Update internal page tracking to reflect the page we've just rendered
    this.lastRenderedPage = targetPage;
    this.firstRenderedPage = targetPage;
    this.loadedPages = new Set([targetPage]);

    this.loadedProducts = this.productContainer ? this.productContainer.querySelectorAll(".collection-product").length : 0;

    // Scroll the clicked product into view
    const product = this.productContainer?.querySelector(`product-card[data-product-handle="${handle}"]`);
    if (product) {
      product.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // Ensure UI (buttons, progress) reflect the restored page
    this.loadingButtonChecker(targetPage);
    if (this.progressBar) this.updateProgress();
  }

  connectedCallback() {
    if (this.automaticLoading === "true") {
      this.observer.observe(this.loadButton);
    }

    // Track current collection
    this.currentCollection = this.getCollectionIdentifier();

    // Clear saved product/page info if coming to a new collection
    this.clearCollectionStateIfNeeded();

    window.removeEventListener("popstate", this._boundPopStateHandler);
    this._boundPopStateHandler = this.handlePopState.bind(this);
    window.addEventListener("popstate", this._boundPopStateHandler);

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.handlePopState();
    };
    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.handlePopState();
    };

    document.addEventListener("click", (e) => {
      const link = e.target.closest("a[href*='/collections/']");
      if (!link) return;

      const newCollection = link.getAttribute("href").match(/\/collections\/([^\/]+)/)[1];
      if (newCollection !== this.currentCollection) {
        this.currentCollection = newCollection;
        this.resetCollectionState();
      }
    });

    this.fetchCurrentPage();

    this.loadedProducts = parseInt(this.loadButton.getAttribute("data-loaded-products-total")) || 0;
    this.totalProducts = parseInt(this.loadButton.getAttribute("data-total-results-count")) || 0;
    this.totalPages = parseInt(this.loadButton.getAttribute("data-total-pages")) || 0;

    this.listenForParamsChange();

    // Restore last clicked product on page load
    window.addEventListener("DOMContentLoaded", async () => {
      const handle = sessionStorage.getItem("lastClickedProduct");
      const page = parseInt(sessionStorage.getItem("lastClickedProductPage"));

      // Overlay to hide awkward loading/scrolling
      if (handle && page) {
        this.showOverlay();
        await this.goToProduct(handle, page);
        this.hideOverlay();
      }
    });

    // Remember clicked product
    document.addEventListener("click", (e) => {
      const link = e.target.closest("product-card a");
      if (!link) return;
      const product = link.closest("product-card");
      if (!product) return;

      const handle = product.dataset.productHandle;
      const page = parseInt(product.dataset.productPage) || 1;

      sessionStorage.setItem("lastClickedProduct", handle);
      sessionStorage.setItem("lastClickedProductPage", page);
    });

    if (this.progressBar) this.updateProgress();

    const currentPage = this.getPageNumberFromUrl(this.url);

    if (currentPage > 1) {
      this.firstRenderedPage = currentPage;

      return;
    }

    //Ensure Load Previous is hidden if on first page
    const loadPreviousButton = this.loadButtonContainer.querySelector('[data-button-type="load-previous"]');
    if (loadPreviousButton && this.firstRenderedPage <= 1) {
      loadPreviousButton.closest(".pagination-wrapper").classList.add("hidden");
    }
  }

  resetCollectionState() {
    // Clear session storage
    sessionStorage.removeItem("lastClickedProduct");
    sessionStorage.removeItem("lastClickedProductPage");

    // Reset internal state
    this.loadedPages = new Set([1]);
    this.firstRenderedPage = 1;
    this.lastRenderedPage = 1;
    this.loadedProducts = 0;
    if (this.productContainer) this.productContainer.innerHTML = "";

    // Reset URL to remove ?page=
    const url = new URL(window.location.href);
    if (url.searchParams.has("page")) {
      url.searchParams.delete("page");
      history.replaceState({ path: url.href }, "", url.href);
    }
    this.url = url.href;
  }

  // Called on load or whenever a collection change is detected
  clearCollectionStateIfNeeded() {
    const newCollection = this.getCollectionIdentifier();
    const currentPage = this.getPageNumberFromUrl(window.location.href);

    if (newCollection !== this.currentCollection) {
      // Clear sessionStorage
      sessionStorage.removeItem("lastClickedProduct");
      sessionStorage.removeItem("lastClickedProductPage");

      // Reset internal state
      this.loadedPages = new Set([1]);
      this.firstRenderedPage = 1;
      this.lastRenderedPage = 1;
      this.loadedProducts = 0;
      if (this.productContainer) this.productContainer.innerHTML = "";

      // Reset URL to remove any ?page=
      const url = new URL(window.location.href);
      if (url.searchParams.has("page")) {
        url.searchParams.delete("page");
        history.replaceState({ path: url.href }, "", url.href);
      }
      this.url = url.href;

      // Update tracked collection
      this.currentCollection = newCollection;
    } else if (currentPage == 1) {
      // Clear sessionStorage
      sessionStorage.removeItem("lastClickedProduct");
      sessionStorage.removeItem("lastClickedProductPage");
    }
  }

  updateUrl() {
    const newUrl = new URL(this.url);
    const pageNum = this.getPageNumberFromUrl(this.url);

    // Only add page param if the collection hasn't just changed
    if (!this.collectionJustChanged) {
      newUrl.searchParams.set("page", pageNum);
    } else {
      // Reset the flag
      this.collectionJustChanged = false;
    }

    this.url = newUrl.href;
    window.history.pushState({ path: newUrl.href }, "", newUrl.href);
  }

  getCollectionIdentifier() {
    // Extract /collections/handle from pathname
    const match = window.location.pathname.match(/^\/collections\/([^\/]+)/);
    return match ? match[1] : "default";
  }

  // Scroll to last clicked product
  scrollToProduct(handle) {
    const product = document.querySelector(`product-card[data-product-handle="${handle}"]`);
    if (product) {
      // Scroll the product into view
      product.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  // Centralised popstate handler (used by push/replace/popstate)
  handlePopState(event) {
    const newCollection = this.getCollectionIdentifier();

    if (newCollection !== this.currentCollection) {
      this.currentCollection = newCollection;
      this.resetCollectionState(); // clear page info and URL
    }

    const url = (event && event.state && event.state.path) || window.location.href;
    const page = this.getPageNumberFromUrl(url) || 1;

    const hasPageInDOM = !!this.productContainer?.querySelector(`.collection-product[data-product-page="${page}"], product-card[data-product-page="${page}"]`);

    if (hasPageInDOM) {
      this.url = url;
      this.lastRenderedPage = Math.max(this.lastRenderedPage, page);
      this.firstRenderedPage = Math.min(this.firstRenderedPage, page);
      this.loadedPages.add(page);
      this.loadedProducts = this.productContainer.querySelectorAll(".collection-product").length;
      this.loadingButtonChecker(page);
      if (this.progressBar) this.updateProgress();
      return;
    }

    this.url = url;
    this.fetchCurrentPageItems(url);
  }

  listenForParamsChange() {
    // We already added the single popstate listener in connectedCallback,
    // so keep this method as a no-op (or leave it out entirely).
  }

  showOverlay() {
    this.overlay.classList.add("active");
  }

  hideOverlay() {
    this.overlay.classList.remove("active");
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0) {
        entry.target.closest("loading-button").loadMore();
      }
    });
  }

  fetchCurrentPage() {
    const url = new URL(window.location.href);
    const page = url.searchParams.get("page");
    if (page) {
      this.url = url.href;
      this.fetchCurrentPageItems(this.url);
    }
  }

  loadMore() {
    const buttonType = this.loadButton.dataset.buttonType;
    let nextUrl;

    if (buttonType === "load-more") {
      const nextPage = this.lastRenderedPage + 1;
      const url = new URL(window.location.href);
      url.searchParams.set("page", nextPage);
      nextUrl = url.href;
    } else {
      const prevPage = this.firstRenderedPage - 1;
      if (prevPage < 1) return; // don't go below 1
      const url = new URL(window.location.href);
      url.searchParams.set("page", prevPage);
      nextUrl = url.href;
    }

    this.url = nextUrl;
    this.loadButton.classList.add("loading");
    this.loadButton.querySelector(".loading-overlay__spinner").classList.remove("hidden");

    this.fetchItems(this.url, buttonType);
  }

  fetchItems(url, buttonType) {
    fetch(url)
      .then((response) => response.text())
      .then((htmlText) => {
        if (url.includes("blog")) {
          this.renderArticleGrid(htmlText, buttonType);
        } else {
          this.renderProductGrid(htmlText, buttonType);
        }

        this.renderNewNavigation(htmlText);
        this.updateUrl();
        document.dispatchEvent(new Event("mobile-gallery:refresh"));
        this.loadingButtonChecker();
        if (this.progressBar) this.updateProgress();
      });
  }

  fetchCurrentPageItems(url) {
    fetch(url)
      .then((response) => response.text())
      .then((htmlText) => {
        if (url.includes("blog")) {
          this.renderCurrentPageArticleGrid(htmlText);
        } else {
          const currentPage = this.getPageNumberFromUrl(url);
          this.loadedPages = new Set([currentPage]);
          this.lastRenderedPage = currentPage;
          this.renderCurrentPageProductGrid(htmlText);
        }

        this.renderNewNavigation(htmlText);

        document.dispatchEvent(new Event("mobile-gallery:refresh"));
        this.loadingButtonChecker();
        if (this.progressBar) this.updateProgress();
      });
  }

  updateUrl() {
    const newUrl = new URL(this.url);
    const pageNum = this.getPageNumberFromUrl(this.url);
    newUrl.searchParams.set("page", pageNum);
    this.url = newUrl.href;
    window.history.pushState({ path: newUrl.href }, "", newUrl.href);
  }

  getPageNumberFromUrl(url) {
    const match = url.match(/page=(\d+)/);
    return match ? parseInt(match[1]) : 1;
  }

  renderProductGrid(html, buttonType) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(html, "text/html");
    const newPageHTML = newDoc.getElementById("ProductGridContainer").innerHTML;
    const newPageNum = this.getPageNumberFromUrl(this.url);

    // Prevent duplicates
    if (this.loadedPages.has(newPageNum)) {
      this.loadButton.classList.remove("loading");
      this.loadButton.querySelector(".loading-overlay__spinner").classList.add("hidden");
      return;
    }

    if (buttonType === "load-more") {
      this.productContainer.insertAdjacentHTML("beforeend", newPageHTML);
      this.lastRenderedPage = Math.max(this.lastRenderedPage, newPageNum);
    } else {
      this.productContainer.insertAdjacentHTML("afterbegin", newPageHTML);
      this.firstRenderedPage = Math.min(this.firstRenderedPage, newPageNum);
    }

    this.loadedPages.add(newPageNum);
    this.loadedProducts = this.productContainer.querySelectorAll(".collection-product").length;
  }

  renderCurrentPageProductGrid(html) {
    const parser = new DOMParser();
    const newPageHTML = parser.parseFromString(html, "text/html").getElementById("ProductGridContainer").innerHTML;

    this.productContainer.innerHTML = newPageHTML;
    this.loadedProducts = this.productContainer.querySelectorAll(".collection-product").length;
  }

  renderArticleGrid(html) {
    const parser = new DOMParser();
    const newPageHTML = parser.parseFromString(html, "text/html").getElementById("ArticleGridContainer").innerHTML;
    this.articleContainer.insertAdjacentHTML("beforeend", newPageHTML);
    this.loadedProducts = this.articleContainer.querySelectorAll(".article").length;
  }

  renderCurrentPageArticleGrid(html) {
    const parser = new DOMParser();
    const newPageHTML = parser.parseFromString(html, "text/html").getElementById("ArticleGridContainer").innerHTML;
    this.articleContainer.innerHTML = newPageHTML;
    this.loadedProducts = this.articleContainer.querySelectorAll(".article").length;
  }

  renderNewNavigation(html) {
    const parser = new DOMParser();
    const paginationHTML = parser.parseFromString(html, "text/html").getElementById("pagination").innerHTML;
    this.vanillaPagination.innerHTML = paginationHTML;
  }

  loadingButtonChecker(page = null) {
    const currentPage = page || this.getPageNumberFromUrl(this.url);

    // Select all load buttons inside the container
    const loadMoreButton = this.loadButtonContainer.querySelector('[data-button-type="load-more"]');
    const loadPreviousButton = this.loadButtonContainer.querySelector('[data-button-type="load-previous"]');

    // Show/hide Load More
    if (loadMoreButton) {
      loadMoreButton.closest(".pagination-wrapper").classList.toggle("hidden", this.lastRenderedPage >= this.totalPages);
    }

    if (loadPreviousButton) {
      loadPreviousButton.closest(".pagination-wrapper").classList.toggle("hidden", this.firstRenderedPage <= 1);
    }

    // Remove loading state from whichever button is visible
    [loadMoreButton, loadPreviousButton].forEach((btn) => {
      if (!btn) return;
      btn.classList.remove("loading");
      const spinner = btn.querySelector(".loading-overlay__spinner");
      if (spinner) spinner.classList.add("hidden");
    });

    // Update progress
    if (this.progressBar) this.updateProgress();
  }

  updateProgress() {
    const progress = (this.loadedProducts / this.totalProducts) * 100;
    this.progressBar.setAttribute("style", "--progress:" + `${progress}` + "%");
    this.resultCount.textContent = `Showing ${this.loadedProducts} of ${this.totalProducts}`;
  }
}

customElements.define("loading-button", loadingButton);

document.addEventListener("DOMContentLoaded", function () {
  function cleanPaginationURL() {
    const currentURL = new URL(window.location);
    if (currentURL.searchParams.has("phcursor")) {
      currentURL.searchParams.delete("phcursor");
      const cleanURL = currentURL.toString();
      if (cleanURL !== window.location.href) {
        history.replaceState({}, "", cleanURL);
      }
    }
  }

  const loadMoreButtons = document.querySelectorAll('loading-button button[rel="nofollow"]');
  loadMoreButtons.forEach((button) => {
    button.addEventListener("click", function (e) {
      setTimeout(cleanPaginationURL, 1000);
    });
  });

  cleanPaginationURL();
});

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    this.progress = parseInt(this.dataset.value);
  }
}

customElements.define("progress-bar", ProgressBar);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

    //this.changeCountPosition();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");
    document.getElementById("ProductGridContainer").classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser().parseFromString(html, "text/html").getElementById("ProductGridContainer").innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, "text/html").getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");

    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;

    const filterToggleEl = new DOMParser().parseFromString(html, "text/html").querySelector(".mobile-filter-toggle");
    const filterToggle = document.querySelector(".mobile-filter-toggle");
    if (filterToggleEl && filterToggle) {
      filterToggle.classList = filterToggleEl.classList;
      document.dispatchEvent(new Event("mobile-gallery:refresh"));
    }

    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter, #FacetFiltersFormFilter .js-filter, #FacetFiltersPillsForm .js-filter");
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    //if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".filter-facets__open", ".filter-facets__count", ".filter-facets__sort", ".drawer__footer .filter-facets__footer"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    //document.getElementById("FacetFiltersFormFilter").closest("side-drawer").bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML = source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML = source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById("ProductGridContainer").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormFilter";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm" || form.id === "FacetSortDrawerForm") {
            const noJsElements = document.querySelectorAll(".no-js-list");
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormFilter") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf("?") == -1 ? "" : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }

  changeCountPosition() {
    const container = document.getElementById("ProductCount");

    if (container && container.closest(".hidden")) {
      const collectionTitle = document.querySelector("h1");

      if (collectionTitle) {
        const isMobile = window.innerWidth < 900;
        if (isMobile && !container.parentElement.isSameNode(collectionTitle)) {
          collectionTitle.insertAdjacentElement("afterbegin", container);
        }
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => element.addEventListener("change", this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "") minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class ScrollableBlock extends HTMLElement {
  constructor() {
    super();

    this.wrapper = this.querySelector(".scrollable-wrapper");
    this.scrollable = this.wrapper;

    this.prevBtn = document.createElement("button");
    this.nextBtn = document.createElement("button");
    this.prevBtn.className = "scroll-btn prev-btn";
    this.nextBtn.className = "scroll-btn next-btn";

    this.prevBtn.hidden = true;
    this.nextBtn.hidden = true;

    this.prepend(this.prevBtn);
    this.append(this.nextBtn);

    this.setupResponsiveMode();
    this.setupEvents();

    requestAnimationFrame(() => this.checkScrollState());
  }

  setupResponsiveMode() {
    const mq = window.matchMedia("(max-width: 750px)");
    const hasVerticalClass = this.classList.contains("desktop-vertical");

    const updateMode = () => {
      if (hasVerticalClass) {
        if (!mq.matches) {
          this.setAttribute("vertical", "");
        }
      }

      this.isVertical = this.hasAttribute("vertical");
      this.prevBtn.innerHTML = this.isVertical ? "↑" : "←";
      this.nextBtn.innerHTML = this.isVertical ? "↓" : "→";
      this.checkScrollState();
    };

    updateMode();
    mq.addEventListener("change", updateMode);
  }

  setupEvents() {
    this.prevBtn.addEventListener("click", () => this.scrollBy(-1));
    this.nextBtn.addEventListener("click", () => this.scrollBy(1));

    this.scrollable.addEventListener("scroll", () => this.checkScrollState());

    const observer = new ResizeObserver(() => this.checkScrollState());
    observer.observe(this.scrollable);
    observer.observe(this);
  }

  scrollBy(direction) {
    const amount = (this.isVertical ? this.scrollable.clientHeight : this.scrollable.clientWidth) * 0.4;

    this.scrollable.scrollBy({
      left: this.isVertical ? 0 : amount * direction,
      top: this.isVertical ? amount * direction : 0,
      behavior: "smooth",
    });
  }

  checkScrollState() {
    requestAnimationFrame(() => {
      if (this.isVertical) {
        const { scrollHeight, clientHeight, scrollTop } = this.scrollable;
        const isScrollable = scrollHeight > clientHeight + 1;
        const tolerance = 5;

        this.prevBtn.hidden = !isScrollable || scrollTop <= tolerance;
        this.nextBtn.hidden = !isScrollable || scrollTop >= scrollHeight - clientHeight - tolerance;
      } else {
        const { scrollWidth, clientWidth, scrollLeft } = this.scrollable;
        const isScrollable = scrollWidth > clientWidth + 1;
        const tolerance = 5;

        this.prevBtn.hidden = !isScrollable || scrollLeft <= tolerance;
        this.nextBtn.hidden = !isScrollable || scrollLeft >= scrollWidth - clientWidth - tolerance;
      }
    });
  }
}

customElements.define("scrollable-block", ScrollableBlock);

// Scrollable Header
document.addEventListener("DOMContentLoaded", () => {
  const headerWrapper = document.querySelector(".header-wrapper");
  const header = headerWrapper.querySelector("header");
  const initScrollY = window.scrollY;
  if (!header) return;

  let lastScrollY = 0;
  let ticking = false;

  if (initScrollY > 0) {
    headerWrapper.classList.add("header--scrollable");
  }

  function updateHeader() {
    const currentScrollY = window.scrollY;

    if (currentScrollY > 0) {
      headerWrapper.classList.add("header--scrollable");
    } else {
      headerWrapper.classList.remove("header--scrollable");
    }

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      headerWrapper.classList.add("header--hidden");
    } else {
      headerWrapper.classList.remove("header--hidden");
    }

    lastScrollY = currentScrollY;
    ticking = false;
  }

  const heroBanner = document.querySelector("main > .hero-banner:first-child .media-content-background");

  if (!heroBanner) {
    headerWrapper.classList.remove("transparent-header");
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }

  window.addEventListener("scroll", onScroll);
});

class RTEFormatter extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll("table").forEach(this.#formatTable);
    this.querySelectorAll("img").forEach(this.#formatImg);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("rte-table-wrapper");
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  #formatImg(img) {
    const wrapper = document.createElement("div");
    const parent = img.closest("p");
    if (parent) parent.classList.add("rte-img-wrapper");
  }
}

if (!customElements.get("rte-formatter")) {
  customElements.define("rte-formatter", RTEFormatter);
}

// Accordions init
document.addEventListener("DOMContentLoaded", function () {
  initAccordions();
  window.addEventListener("resize", syncOpenHeights, { passive: true });
});

document.addEventListener("product:refresh", function () {
  initAccordions();
});

function initAccordions() {
  const allAccordions = document.querySelectorAll(".accordion");
  allAccordions.forEach(initAccordion);
}

document.addEventListener("accordion:init", (event) => {
  const accordion = event.detail?.accordion;
  if (accordion) {
    initAccordion(accordion);
  }
});

function initAccordion(accordionEl) {
  if (accordionEl.hasAttribute("data-accordion-initialized")) return;

  const header = accordionEl.querySelector(".accordion__header");
  const content = accordionEl.querySelector(".accordion__content");
  const icon = accordionEl.querySelector(".accordion__icon");
  const shouldOpen = accordionEl.dataset.open === "true";

  if (shouldOpen) {
    content.style.maxHeight = content.scrollHeight + "px";
    header.setAttribute("aria-expanded", "true");
    if (icon) icon.classList.add("is-open");
  } else {
    content.style.maxHeight = 0;
    header.setAttribute("aria-expanded", "false");
    if (icon) icon.classList.remove("is-open");
  }

  header.addEventListener("click", function () {
    const isOpen = header.getAttribute("aria-expanded") === "true";
    if (isOpen) {
      content.style.maxHeight = 0;
      if (icon) icon.classList.remove("is-open");
      header.setAttribute("aria-expanded", "false");
    } else {
      content.style.maxHeight = content.scrollHeight + "px";
      if (icon) icon.classList.add("is-open");
      header.setAttribute("aria-expanded", "true");
    }
  });

  accordionEl.setAttribute("data-accordion-initialized", "true");
}

function syncOpenHeights() {
  document.querySelectorAll('.accordion .accordion__header[aria-expanded="true"]').forEach((header) => {
    const content = header.parentElement.querySelector(".accordion__content");
    if (content) content.style.maxHeight = content.scrollHeight + "px";
  });
}

//Init mobile featured blocks Sliders
document.addEventListener("DOMContentLoaded", function () {
  const sliders = document.querySelectorAll(".mobile-featured-blocks-slider");
  const PROGRESS_BAR_SELECTOR = ".menu-featured-blocks-progress";
  const instances = new Map();
  if (!sliders.length) return;

  sliders.forEach((splideEl) => {
    if (!splideEl || splideEl.dataset.mounted === "1") return;

    const carousel = new Splide(splideEl, {
      perPage: 2,
      perMove: 1,
      pagination: false,
      arrows: false,
      speed: 600,
      autoplay: true,
      interval: 3000,
      pauseOnHover: true,
      type: "loop",
      breakpoints: {
        450: { perPage: 1 },
      },
    });
    const redraw = () => updateProgress(splideEl, carousel);
    carousel.on("mounted move updated refresh", redraw);

    carousel.mount();
    instances.set(splideEl, carousel);
    redraw();
    splideEl.dataset.mounted = "1";
  });

  function resolveProgressBar(el) {
    const parent = el.parentElement;
    let bar = parent ? parent.querySelector(PROGRESS_BAR_SELECTOR) : null;
    if (!bar) bar = el.querySelector(PROGRESS_BAR_SELECTOR);
    return bar || null;
  }

  function updateProgress(el, splide) {
    const bar = resolveProgressBar(el);
    if (!bar) return;
    const end = splide.Components.Controller.getEnd() + 1;
    const rate = Math.min((splide.index + 1) / end, 1);
    bar.style.width = `${rate * 100}%`;
  }
});

// Featured Product Card
class FeaturedProduct extends HTMLElement {
  constructor() {
    super();
    this.productCard = this.querySelector("product-card");
    this.media = this.querySelector(".product-card__media-image");
    this.setupEvents();
  }

  setupEvents() {
    if (!this.productCard || !this.media) return;

    this.media.addEventListener("click", (event) => {
      if (!this.productCard.classList.contains("active")) {
        event.preventDefault();
        event.stopPropagation();
        this.productCard.classList.add("active");
      } else {
        window.location = this.media.href;
      }
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) {
        this.productCard.classList.remove("active");
      }
    });
  }
}

customElements.define("featured-product", FeaturedProduct);
